import { toast } from "react-toastify"

import { REQ } from "constants/api/requests"
import { IResponse } from "constants/interfaces"

import { Paginated } from "services/user/type"

import {
  ChatMessagesResponse,
  GetParticipantsParams,
  GetTournamentsParams,
  PointRule,
  RulePreset,
  Tournament,
  TournamentAnalytics,
  TournamentParticipant,
  TournamentReferralData
} from "types/tournament.types"

import useApi, { parseError } from "util/api"

export const TournamentService = {
  /**
   * Get list of tournaments with pagination and filters
   */
  async getTournamentsList(
    page: number,
    pageSize: number,
    _id?: any,
    filters?: {
      q?: string
      status?: string
      scoring_mode?: string
    },
    sortRow?: string,
    sortOrder?: string
  ): Promise<Paginated<Tournament[]> | void> {
    try {
      const params: GetTournamentsParams = {
        page,
        pageSize,
        ...(filters || {}),
        sortRow,
        sortOrder: sortOrder as "asc" | "desc"
      }

      const { data } = await useApi.get<
        IResponse<{ data: Tournament[]; total: number; page: number; pageSize: number; totalPages: number }>
      >(REQ.ADMIN_DASHBOARD.TOURNAMENTS.LIST, { params })

      if (data?.success) {
        return {
          data: data.response.data,
          total: data.response.total,
          page: data.response.page,
          pageSize: data.response.pageSize,
          total_pages: data.response.totalPages
        }
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to fetch tournaments")
      parseError(error)
    }
  },

  /**
   * Get single tournament by ID
   */
  async getTournament(id: string): Promise<Tournament | void> {
    try {
      const { data } = await useApi.get<IResponse<Tournament>>(REQ.ADMIN_DASHBOARD.TOURNAMENTS.GET.replace(":id", id))

      if (data?.success) {
        return data?.response
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to fetch tournament")
      parseError(error)
    }
  },

  /**
   * Create a new tournament
   */
  async createTournament(payload: Partial<Tournament>): Promise<Tournament | void> {
    try {
      const { data } = await useApi.post<IResponse<Tournament>>(REQ.ADMIN_DASHBOARD.TOURNAMENTS.CREATE, payload)

      if (data?.success) {
        return data?.response
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to create tournament")
      parseError(error)
      throw error
    }
  },

  /**
   * Update existing tournament
   */
  async updateTournament(id: string, payload: Partial<Tournament>): Promise<Tournament | void> {
    try {
      const { data } = await useApi.patch<IResponse<Tournament>>(REQ.ADMIN_DASHBOARD.TOURNAMENTS.UPDATE.replace(":id", id), payload)

      if (data?.success) {
        return data?.response
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to update tournament")
      parseError(error)
      throw error
    }
  },

  /**
   * Delete (cancel) tournament
   */
  async deleteTournament(id: string): Promise<void> {
    try {
      const { data } = await useApi.delete<IResponse<void>>(REQ.ADMIN_DASHBOARD.TOURNAMENTS.DELETE.replace(":id", id))

      if (data?.success) {
        return data?.response
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to delete tournament")
      parseError(error)
      throw error
    }
  },

  /**
   * Permanently delete a cancelled tournament
   */
  async permanentlyDeleteTournament(id: string): Promise<void> {
    try {
      const { data } = await useApi.delete<IResponse<void>>(REQ.ADMIN_DASHBOARD.TOURNAMENTS.PERMANENT_DELETE.replace(":id", id))

      if (data?.success) {
        return data?.response
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to permanently delete tournament")
      parseError(error)
      throw error
    }
  },

  /**
   * Publish a draft tournament (transition DRAFT → SCHEDULED)
   */
  async publishTournament(id: string): Promise<Tournament | void> {
    try {
      const { data } = await useApi.patch<IResponse<Tournament>>(REQ.ADMIN_DASHBOARD.TOURNAMENTS.UPDATE.replace(":id", id), {
        status: "SCHEDULED"
      })

      if (data?.success) {
        return data?.response
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to publish tournament")
      parseError(error)
      throw error
    }
  },

  /**
   * Duplicate a tournament as a new DRAFT copy
   */
  async duplicateTournament(id: string): Promise<Tournament | void> {
    try {
      const { data } = await useApi.post<IResponse<Tournament>>(REQ.ADMIN_DASHBOARD.TOURNAMENTS.DUPLICATE.replace(":id", id))

      if (data?.success) {
        return data?.response
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to duplicate tournament")
      parseError(error)
      throw error
    }
  },

  /**
   * Start a scheduled tournament
   */
  async startTournament(id: string): Promise<Tournament | void> {
    try {
      const { data } = await useApi.post<IResponse<Tournament>>(REQ.ADMIN_DASHBOARD.TOURNAMENTS.START.replace(":id", id))

      if (data?.success) {
        return data?.response
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to start tournament")
      parseError(error)
      throw error
    }
  },

  /**
   * End an active tournament
   */
  async endTournament(id: string): Promise<Tournament | void> {
    try {
      const { data } = await useApi.post<IResponse<Tournament>>(REQ.ADMIN_DASHBOARD.TOURNAMENTS.END.replace(":id", id))

      if (data?.success) {
        return data?.response
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to end tournament")
      parseError(error)
      throw error
    }
  },

  /**
   * Get tournament participants
   */
  async getTournamentParticipants(
    id: string,
    page: number,
    pageSize: number,
    filters?: {
      q?: string
      sortRow?: string
      sortOrder?: string
    }
  ): Promise<Paginated<TournamentParticipant[]> | void> {
    try {
      const params: GetParticipantsParams = {
        page,
        pageSize,
        q: filters?.q,
        sortRow: filters?.sortRow,
        sortOrder: filters?.sortOrder as "asc" | "desc" | undefined
      }

      const { data } = await useApi.get<
        IResponse<{ data: TournamentParticipant[]; total: number; page: number; pageSize: number; totalPages: number }>
      >(REQ.ADMIN_DASHBOARD.TOURNAMENTS.PARTICIPANTS.replace(":id", id), { params })

      if (data?.success) {
        return {
          data: data.response.data,
          total: data.response.total,
          page: data.response.page,
          pageSize: data.response.pageSize,
          total_pages: data.response.totalPages
        }
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to fetch participants")
      parseError(error)
    }
  },

  /**
   * Get tournament analytics
   */
  async getTournamentAnalytics(id: string): Promise<TournamentAnalytics | void> {
    try {
      const { data } = await useApi.get<IResponse<TournamentAnalytics>>(REQ.ADMIN_DASHBOARD.TOURNAMENTS.ANALYTICS.replace(":id", id))

      if (data?.success) {
        return data?.response
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to fetch analytics")
      parseError(error)
    }
  },

  /**
   * Distribute prizes manually
   */
  async distributePrizes(id: string): Promise<any> {
    try {
      const { data } = await useApi.post<IResponse<any>>(REQ.ADMIN_DASHBOARD.TOURNAMENTS.DISTRIBUTE_PRIZES.replace(":id", id))

      if (data?.success) {
        return data?.response
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to distribute prizes")
      parseError(error)
      throw error
    }
  },

  /**
   * Force recalculate leaderboard
   */
  async recalculateLeaderboard(id: string): Promise<any> {
    try {
      const { data } = await useApi.post<IResponse<any>>(REQ.ADMIN_DASHBOARD.TOURNAMENTS.RECALCULATE_LEADERBOARD.replace(":id", id))

      if (data?.success) {
        return data?.response
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to recalculate leaderboard")
      parseError(error)
      throw error
    }
  },

  /**
   * Get disqualification statistics
   */
  async getDisqualificationStats(id: string): Promise<any> {
    try {
      const { data } = await useApi.get<IResponse<any>>(REQ.ADMIN_DASHBOARD.TOURNAMENTS.DISQUALIFICATION_STATS.replace(":id", id))

      if (data?.success) {
        return data?.response
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to fetch disqualification stats")
      parseError(error)
    }
  },

  /**
   * Get chat messages for a tournament (admin - includes deleted)
   */
  async getChatMessages(id: string, page = 1, limit = 200, onlyReported = false, playerId?: string): Promise<ChatMessagesResponse | void> {
    try {
      const { data } = await useApi.get<IResponse<ChatMessagesResponse>>(REQ.ADMIN_DASHBOARD.TOURNAMENTS.CHAT_MESSAGES.replace(":id", id), {
        params: {
          page,
          limit,
          ...(onlyReported ? { onlyReported: "true" } : {}),
          ...(playerId ? { playerId } : {})
        }
      })

      if (data?.success) {
        return data?.response
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to fetch chat messages")
      parseError(error)
    }
  },

  /**
   * Soft-delete a chat message
   */
  async deleteChatMessage(tournamentId: string, messageId: string): Promise<any> {
    try {
      const { data } = await useApi.delete<IResponse<any>>(
        REQ.ADMIN_DASHBOARD.TOURNAMENTS.CHAT_DELETE_MESSAGE.replace(":id", tournamentId).replace(":messageId", messageId)
      )

      if (data?.success) {
        return data?.response
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to delete message")
      parseError(error)
      throw error
    }
  },

  /**
   * Restore a soft-deleted chat message
   */
  async restoreChatMessage(tournamentId: string, messageId: string): Promise<any> {
    try {
      const { data } = await useApi.post<IResponse<any>>(
        REQ.ADMIN_DASHBOARD.TOURNAMENTS.CHAT_RESTORE_MESSAGE.replace(":id", tournamentId).replace(":messageId", messageId)
      )

      if (data?.success) {
        return data?.response
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to restore message")
      parseError(error)
      throw error
    }
  },

  /**
   * Get tournament referral rewards data
   */
  async getReferralRewards(id: string): Promise<TournamentReferralData | void> {
    try {
      const { data } = await useApi.get<IResponse<TournamentReferralData>>(
        REQ.ADMIN_DASHBOARD.TOURNAMENTS.REFERRAL_REWARDS.replace(":id", id)
      )

      if (data?.success) {
        return data?.response
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to fetch referral data")
      parseError(error)
    }
  },

  // =========================================================================
  // Rule Presets
  // =========================================================================

  /**
   * List all rule presets (unpaginated, for dropdowns)
   */
  async listPresets(): Promise<RulePreset[] | void> {
    try {
      const { data } = await useApi.get<IResponse<{ data: RulePreset[]; total: number }>>(REQ.ADMIN_DASHBOARD.TOURNAMENTS.PRESETS_LIST, {
        params: { limit: 200 }
      })

      if (data?.success) {
        const response = data.response as any
        return Array.isArray(response) ? response : response?.data ?? []
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to fetch presets")
      parseError(error)
    }
  },

  /**
   * List rule presets with pagination
   */
  async listPresetsPaginated(page = 1, pageSize = 10, search?: string): Promise<Paginated<RulePreset> | void> {
    try {
      const params: Record<string, any> = { page, limit: pageSize }
      if (search) params.q = search

      const { data } = await useApi.get<
        IResponse<{ data: RulePreset[]; total: number; page: number; pageSize: number; totalPages: number }>
      >(REQ.ADMIN_DASHBOARD.TOURNAMENTS.PRESETS_LIST, { params })

      if (data?.success) {
        const response = data.response as any
        return {
          data: Array.isArray(response) ? response : response?.data ?? [],
          total: response?.total ?? 0,
          page: response?.page ?? page,
          pageSize: response?.pageSize ?? pageSize,
          total_pages: response?.totalPages ?? 1
        }
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to fetch presets")
      parseError(error)
    }
  },

  /**
   * Create a new rule preset
   */
  async createPreset(payload: { name: string; description?: string; rules: PointRule[] }): Promise<RulePreset | void> {
    try {
      const { data } = await useApi.post<IResponse<RulePreset>>(REQ.ADMIN_DASHBOARD.TOURNAMENTS.PRESETS_CREATE, payload)

      if (data?.success) {
        return data?.response
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to create preset")
      parseError(error)
      throw error
    }
  },

  /**
   * Update a rule preset
   */
  async updatePreset(id: string, payload: { name?: string; description?: string; rules?: PointRule[] }): Promise<RulePreset | void> {
    try {
      const { data } = await useApi.patch<IResponse<RulePreset>>(
        REQ.ADMIN_DASHBOARD.TOURNAMENTS.PRESETS_UPDATE.replace(":presetId", id),
        payload
      )

      if (data?.success) {
        return data?.response
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to update preset")
      parseError(error)
      throw error
    }
  },

  /**
   * Delete a rule preset
   */
  async deletePreset(id: string): Promise<void> {
    try {
      const { data } = await useApi.delete<IResponse<void>>(REQ.ADMIN_DASHBOARD.TOURNAMENTS.PRESETS_DELETE.replace(":presetId", id))

      if (data?.success) {
        return data?.response
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to delete preset")
      parseError(error)
      throw error
    }
  },

  /**
   * Seed default rule presets from the platform template library
   */
  async seedDefaultPresets(): Promise<{ created: number; skipped: number; names: string[] } | void> {
    try {
      const { data } = await useApi.post<IResponse<{ created: number; skipped: number; names: string[] }>>(
        REQ.ADMIN_DASHBOARD.TOURNAMENTS.PRESETS_SEED
      )

      if (data?.success) {
        return data?.response
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to seed default presets")
      parseError(error)
      throw error
    }
  }
}
